import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Leaf, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirm_password: '',
    full_name: '',
    role: 'grower'
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordsMatch = formData.password === formData.confirm_password;
  const passwordValid = formData.password.length >= 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin) {
      if (!passwordValid) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (!passwordsMatch) {
        toast.error('Passwords do not match');
        return;
      }
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { username: formData.username, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, payload);

      if (isLogin) {
        toast.success('Login successful!');
        onLogin(response.data.user, response.data.token);
      } else {
        // Check if account needs approval
        if (response.data.status === 'pending') {
          toast.success('Registration submitted! Your account is pending approval by an owner.', {
            duration: 5000
          });
        } else {
          toast.success('Registration successful! Please login.');
        }
        setIsLogin(true);
        setFormData({ username: '', password: '', confirm_password: '', full_name: '', role: 'grower' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.pexels.com/photos/18086806/pexels-photo-18086806.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')` }}
      >
        <div className="absolute inset-0 bg-[#1A4D2E] bg-opacity-70 flex items-center justify-center">
          <div className="text-center text-white px-8">
            <Leaf className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>A3ROS</h1>
            <p className="text-xl text-gray-100">Precision Agriculture for Mushroom Farming</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9FAF9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <Leaf className="w-16 h-16 mx-auto mb-4 text-[#1A4D2E]" />
            <h1 className="text-4xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>A3ROS</h1>
          </div>

          <Card className="border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">{isLogin ? 'Login' : 'Register'}</CardTitle>
              <CardDescription>
                {isLogin 
                  ? 'Access your mushroom farm dashboard' 
                  : 'Create an account (requires owner approval)'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="username-input"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    className="mt-1 bg-gray-50/50 focus:ring-[#1A4D2E]/20"
                  />
                </div>

                {!isLogin && (
                  <div>
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      data-testid="fullname-input"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      className="mt-1 bg-gray-50/50 focus:ring-[#1A4D2E]/20"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="bg-gray-50/50 focus:ring-[#1A4D2E]/20 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {!isLogin && formData.password && (
                    <p className={`text-xs mt-1 flex items-center gap-1 ${passwordValid ? 'text-green-600' : 'text-red-500'}`}>
                      {passwordValid ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      {passwordValid ? 'Password length OK' : 'Minimum 6 characters required'}
                    </p>
                  )}
                </div>

                {!isLogin && (
                  <div>
                    <Label htmlFor="confirm_password">Confirm Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="confirm_password"
                        data-testid="confirm-password-input"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirm_password}
                        onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                        required
                        className={`bg-gray-50/50 focus:ring-[#1A4D2E]/20 pr-10 ${
                          formData.confirm_password && !passwordsMatch ? 'border-red-500' : ''
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formData.confirm_password && (
                      <p className={`text-xs mt-1 flex items-center gap-1 ${passwordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordsMatch ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                        {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                      </p>
                    )}
                  </div>
                )}

                {!isLogin && (
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <select
                      id="role"
                      data-testid="role-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                    >
                      <option value="grower">Grower</option>
                      <option value="composting_supervisor">Composting Supervisor</option>
                      <option value="growing_supervisor">Growing Supervisor</option>
                      <option value="owner">Owner</option>
                      <option value="temperature_tech">Temperature Technician</option>
                      <option value="water_tech">Water Technician</option>
                    </select>
                  </div>
                )}

                {!isLogin && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>New accounts require approval from an owner before you can login.</span>
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="submit-button"
                  disabled={loading || (!isLogin && (!passwordValid || !passwordsMatch))}
                  className="w-full bg-[#1A4D2E] hover:bg-[#143D23] text-white rounded-lg py-6 text-base font-medium"
                >
                  {loading ? 'Processing...' : isLogin ? 'Login' : 'Register'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  data-testid="toggle-auth-button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setFormData({ username: '', password: '', confirm_password: '', full_name: '', role: 'grower' });
                  }}
                  className="text-[#1A4D2E] hover:underline text-sm font-medium"
                >
                  {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
